import { APP } from '../constants/app.constants';
import { parseCssColor, pickContrastingForeground } from '../utils/contrast.util';

/** Tenant branding — mirrors `branding_json` in `api-service` (`BrandingValidator`). */

export type BrandingStatus = 'DRAFT' | 'ACTIVE' | 'INACTIVE';
export type BrandingTheme = 'light' | 'dark';
export type BrandingSummaryState = 'DEFAULT' | 'DRAFT' | 'PUBLISHED';

export interface ThemeColors {
	primary?: string;
	accent?: string;
	accentHover?: string;
	/** Page background. Text on it is derived, never stored. */
	background?: string;
	/** Cards, panels, table headers and other raised containers. */
	surface?: string;
	/** Dividers, input and card borders. */
	border?: string;
	sidebar?: string;
	sidebarBorder?: string;
	sidebarActive?: string;
}

export interface BrandingConfig {
	schemaVersion: 1;
	identity?: { appName?: string; appTitle?: string; brandName?: string; assistantName?: string };
	logo?: {
		wordmark?: { light?: string; dark?: string };
		mark?: { light?: string; dark?: string };
		favicon?: string;
	};
	colors?: { light?: ThemeColors; dark?: ThemeColors };
	login?: { backgroundImage?: string };
	footer?: { text?: string };
	email?: { senderName?: string; logoUrl?: string };
}

/** Every key present, for form binding. Empty strings mean "not set". */
export interface EditableBranding {
	schemaVersion: 1;
	identity: { appName: string; appTitle: string; brandName: string; assistantName: string };
	logo: { wordmark: { light: string; dark: string }; mark: { light: string; dark: string }; favicon: string };
	colors: { light: Required<ThemeColors>; dark: Required<ThemeColors> };
	login: { backgroundImage: string };
	footer: { text: string };
	email: { senderName: string; logoUrl: string };
}

export interface BrandingWarning {
	field: string;
	type: string;
	ratio: number;
	minimum: number;
	message: string;
}

export interface TenantBrandingResponse {
	id: string;
	tenantId: string;
	tenantName?: string | null;
	tenantCode?: string | null;
	status: BrandingStatus;
	version: number | null;
	description?: string | null;
	branding: BrandingConfig;
	publishedAt?: string | null;
	publishedBy?: string | null;
	publishedByName?: string | null;
	sourceVersion?: number | null;
	warnings?: BrandingWarning[];
	createdAt?: string;
	updatedAt?: string;
	createdBy?: string | null;
	updatedBy?: string | null;
}

export interface TenantBrandingMeResponse {
	tenantId: string;
	version: number;
	branding: BrandingConfig;
}

export interface BrandingSwatches {
	lightPrimary?: string | null;
	lightAccent?: string | null;
	lightSidebar?: string | null;
	darkPrimary?: string | null;
}

export interface TenantBrandingSummary {
	tenantId: string;
	tenantName?: string | null;
	tenantCode?: string | null;
	state: BrandingSummaryState;
	activeVersion?: number | null;
	hasDraft: boolean;
	appName?: string | null;
	swatches?: BrandingSwatches | null;
	lastChangedAt?: string | null;
	lastChangedByName?: string | null;
}

export interface BrandingCounts {
	tenants: number;
	published: number;
	draftPending: number;
	platformDefault: number;
}

export interface PagedBrandingResponse<T> {
	content?: T[];
	pageNumber?: number;
	pageSize?: number;
	totalElements?: number;
	totalPages?: number;
}

// ── Platform defaults (today's values) ─────────────────────────────────────

/** Built-in look. Empty logo slots mean "keep the bundled ARMA asset". */
export const DEFAULT_BRANDING: EditableBranding = {
	schemaVersion: 1,
	identity: {
		appName: APP.NAME,
		appTitle: 'ARMA - AI-Native Regulatory & Supervisory Platform',
		brandName: 'ARMA',
		assistantName: '',
	},
	logo: { wordmark: { light: '', dark: '' }, mark: { light: '', dark: '' }, favicon: '' },
	colors: {
		light: { primary: '#000000', accent: '#0F172A', accentHover: '#1E293B', background: '#FFFFFF', surface: '#F9F9F9', border: '#E4E4E7', sidebar: '#FBFBFB', sidebarBorder: '#E4E4E7', sidebarActive: '#000000' },
		dark: { primary: '#FAFAFA', accent: '#FAFAFA', accentHover: '#E4E4E7', background: '#09090B', surface: '#18181B', border: '#3F3F46', sidebar: '#18181B', sidebarBorder: '#3F3F46', sidebarActive: '#FAFAFA' },
	},
	login: { backgroundImage: '' },
	footer: { text: '© ARMA' },
	email: { senderName: '', logoUrl: '' },
};

export const THEME_COLOR_KEYS = ['primary', 'accent', 'accentHover'] as const;
export const SURFACE_COLOR_KEYS = ['background', 'surface', 'border'] as const;
export const SIDEBAR_COLOR_KEYS = ['sidebar', 'sidebarBorder', 'sidebarActive'] as const;
export type ThemeColorKey = (typeof THEME_COLOR_KEYS)[number];
export type SurfaceColorKey = (typeof SURFACE_COLOR_KEYS)[number];
export type SidebarColorKey = (typeof SIDEBAR_COLOR_KEYS)[number];

// ── Presets (UI-only; the API stores hex values, never a preset id) ────────

export type ThemeColorSet = Record<ThemeColorKey, string>;
export type SidebarColorSet = Record<SidebarColorKey, string>;
/** Everything a theme preset sets for one mode: brand colors, page surfaces and sidebar. */
export type ThemePalette = Record<ThemeColorKey | SurfaceColorKey | SidebarColorKey, string>;

export interface ColorPreset {
	id: string;
	name: string;
	light: ThemePalette;
	dark: ThemePalette;
}

export interface SidebarPreset {
	id: string;
	name: string;
	light: SidebarColorSet;
	dark: SidebarColorSet;
}

/**
 * Full palettes: picking one re-themes buttons, page and card surfaces, borders and the sidebar.
 * Every value keeps derived text at WCAG AA (asserted in `branding.models.spec.ts`).
 */
export const COLOR_PRESETS: readonly ColorPreset[] = [
	{ id: 'arma', name: 'ARMA default', light: { ...DEFAULT_BRANDING.colors.light }, dark: { ...DEFAULT_BRANDING.colors.dark } },
	{ id: 'ocean', name: 'Ocean', light: { primary: '#1A73E8', accent: '#0B3D91', accentHover: '#0F4FB8', background: '#F9FCFF', surface: '#EFF6FF', border: '#BFDBFE', sidebar: '#DBEAFE', sidebarBorder: '#BFDBFE', sidebarActive: '#1D4ED8' }, dark: { primary: '#8AB4F8', accent: '#D2E3FC', accentHover: '#AECBFA', background: '#0D1121', surface: '#181F38', border: '#233266', sidebar: '#10172E', sidebarBorder: '#233266', sidebarActive: '#93C5FD' } },
	{ id: 'indigo', name: 'Indigo', light: { primary: '#4F46E5', accent: '#312E81', accentHover: '#3730A3', background: '#F9FAFF', surface: '#EEF2FF', border: '#C7D2FE', sidebar: '#E0E7FF', sidebarBorder: '#C7D2FE', sidebarActive: '#4338CA' }, dark: { primary: '#A5B4FC', accent: '#E0E7FF', accentHover: '#C7D2FE', background: '#0F0E1E', surface: '#1B1A33', border: '#2E2B60', sidebar: '#141229', sidebarBorder: '#2E2B60', sidebarActive: '#A5B4FC' } },
	{ id: 'emerald', name: 'Emerald', light: { primary: '#047857', accent: '#064E3B', accentHover: '#065F46', background: '#F8FEFC', surface: '#ECFDF5', border: '#A7F3D0', sidebar: '#D1FAE5', sidebarBorder: '#A7F3D0', sidebarActive: '#047857' }, dark: { primary: '#34D399', accent: '#D1FAE5', accentHover: '#A7F3D0', background: '#071412', surface: '#0D221F', border: '#184037', sidebar: '#071A17', sidebarBorder: '#184037', sidebarActive: '#6EE7B7' } },
	{ id: 'teal', name: 'Teal', light: { primary: '#0F766E', accent: '#134E4A', accentHover: '#115E59', background: '#FAFEFD', surface: '#F0FDFA', border: '#99F6E4', sidebar: '#CCFBF1', sidebarBorder: '#99F6E4', sidebarActive: '#0F766E' }, dark: { primary: '#5EEAD4', accent: '#CCFBF1', accentHover: '#99F6E4', background: '#081416', surface: '#0E2425', border: '#1D403F', sidebar: '#081B1C', sidebarBorder: '#1D403F', sidebarActive: '#5EEAD4' } },
	{ id: 'crimson', name: 'Crimson', light: { primary: '#B91C1C', accent: '#7F1D1D', accentHover: '#991B1B', background: '#FFFAFA', surface: '#FEF2F2', border: '#FECACA', sidebar: '#FEE2E2', sidebarBorder: '#FECACA', sidebarActive: '#B91C1C' }, dark: { primary: '#FCA5A5', accent: '#FEE2E2', accentHover: '#FECACA', background: '#1B090B', surface: '#2F1113', border: '#5A2223', sidebar: '#250B0C', sidebarBorder: '#5A2223', sidebarActive: '#FCA5A5' } },
	{ id: 'amber', name: 'Amber', light: { primary: '#B45309', accent: '#78350F', accentHover: '#92400E', background: '#FFFEF8', surface: '#FFFBEB', border: '#FDE68A', sidebar: '#FEF3C7', sidebarBorder: '#FDE68A', sidebarActive: '#B45309' }, dark: { primary: '#FCD34D', accent: '#FEF3C7', accentHover: '#FDE68A', background: '#1B0E09', surface: '#2F190F', border: '#57321D', sidebar: '#251209', sidebarBorder: '#57321D', sidebarActive: '#FCD34D' } },
	{ id: 'graphite', name: 'Graphite', light: { primary: '#374151', accent: '#111827', accentHover: '#1F2937', background: '#FDFDFE', surface: '#F9FAFB', border: '#E5E7EB', sidebar: '#F3F4F6', sidebarBorder: '#E5E7EB', sidebarActive: '#374151' }, dark: { primary: '#D1D5DB', accent: '#F3F4F6', accentHover: '#E5E7EB', background: '#07080D', surface: '#0E1017', border: '#232830', sidebar: '#070910', sidebarBorder: '#232830', sidebarActive: '#D1D5DB' } },
	{ id: 'violet', name: 'Violet', light: { primary: '#6D28D9', accent: '#4C1D95', accentHover: '#5B21B6', background: '#FCFBFF', surface: '#F5F3FF', border: '#DDD6FE', sidebar: '#EDE9FE', sidebarBorder: '#DDD6FE', sidebarActive: '#6D28D9' }, dark: { primary: '#C4B5FD', accent: '#EDE9FE', accentHover: '#DDD6FE', background: '#140B26', surface: '#231440', border: '#3E2469', sidebar: '#1B0D35', sidebarBorder: '#3E2469', sidebarActive: '#C4B5FD' } },
	{ id: 'rose', name: 'Rose', light: { primary: '#BE123C', accent: '#881337', accentHover: '#9F1239', background: '#FFFAFA', surface: '#FFF1F2', border: '#FECDD3', sidebar: '#FFE4E6', sidebarBorder: '#FECDD3', sidebarActive: '#BE123C' }, dark: { primary: '#FDA4AF', accent: '#FFE4E6', accentHover: '#FECDD3', background: '#1D080F', surface: '#320F1A', border: '#5D1E31', sidebar: '#280813', sidebarBorder: '#5D1E31', sidebarActive: '#FDA4AF' } },
	{ id: 'sky', name: 'Sky', light: { primary: '#0369A1', accent: '#0C4A6E', accentHover: '#075985', background: '#FAFDFF', surface: '#F0F9FF', border: '#BAE6FD', sidebar: '#E0F2FE', sidebarBorder: '#BAE6FD', sidebarActive: '#0369A1' }, dark: { primary: '#7DD3FC', accent: '#E0F2FE', accentHover: '#BAE6FD', background: '#09141E', surface: '#102432', border: '#193E53', sidebar: '#0A1B29', sidebarBorder: '#193E53', sidebarActive: '#7DD3FC' } },
	{ id: 'lagoon', name: 'Lagoon', light: { primary: '#0E7490', accent: '#164E63', accentHover: '#155E75', background: '#F8FFFF', surface: '#ECFEFF', border: '#A5F3FC', sidebar: '#CFFAFE', sidebarBorder: '#A5F3FC', sidebarActive: '#0E7490' }, dark: { primary: '#67E8F9', accent: '#CFFAFE', accentHover: '#A5F3FC', background: '#09161C', surface: '#102630', border: '#1F404C', sidebar: '#0A1D26', sidebarBorder: '#1F404C', sidebarActive: '#67E8F9' } },
	{ id: 'slate', name: 'Slate', light: { primary: '#334155', accent: '#0F172A', accentHover: '#1E293B', background: '#FDFDFE', surface: '#F8FAFC', border: '#E2E8F0', sidebar: '#F1F5F9', sidebarBorder: '#E2E8F0', sidebarActive: '#334155' }, dark: { primary: '#CBD5E1', accent: '#F1F5F9', accentHover: '#E2E8F0', background: '#07080F', surface: '#0D0F19', border: '#232832', sidebar: '#070912', sidebarBorder: '#232832', sidebarActive: '#CBD5E1' } },
	{ id: 'copper', name: 'Copper', light: { primary: '#C2410C', accent: '#7C2D12', accentHover: '#9A3412', background: '#FFFCF9', surface: '#FFF7ED', border: '#FED7AA', sidebar: '#FFEDD5', sidebarBorder: '#FED7AA', sidebarActive: '#C2410C' }, dark: { primary: '#FDBA74', accent: '#FFEDD5', accentHover: '#FED7AA', background: '#1A0C0A', surface: '#2E1611', border: '#5B2D1F', sidebar: '#240F0B', sidebarBorder: '#5B2D1F', sidebarActive: '#FDBA74' } },
	{ id: 'plum', name: 'Plum', light: { primary: '#7E22CE', accent: '#581C87', accentHover: '#6B21A8', background: '#FDFCFF', surface: '#FAF5FF', border: '#E9D5FF', sidebar: '#F3E8FF', sidebarBorder: '#E9D5FF', sidebarActive: '#7E22CE' }, dark: { primary: '#D8B4FE', accent: '#F3E8FF', accentHover: '#E9D5FF', background: '#180826', surface: '#2A1040', border: '#462463', sidebar: '#210935', sidebarBorder: '#462463', sidebarActive: '#D8B4FE' } },
	{ id: 'orchid', name: 'Orchid', light: { primary: '#A21CAF', accent: '#701A75', accentHover: '#86198F', background: '#FEFBFF', surface: '#FDF4FF', border: '#F5D0FE', sidebar: '#FAE8FF', sidebarBorder: '#F5D0FE', sidebarActive: '#A21CAF' }, dark: { primary: '#F0ABFC', accent: '#FAE8FF', accentHover: '#F5D0FE', background: '#1D081F', surface: '#310E35', border: '#522157', sidebar: '#27082B', sidebarBorder: '#522157', sidebarActive: '#F0ABFC' } },
	{ id: 'sandstone', name: 'Sandstone', light: { primary: '#44403C', accent: '#1C1917', accentHover: '#292524', background: '#FDFDFD', surface: '#FAFAF9', border: '#E7E5E4', sidebar: '#F5F5F4', sidebarBorder: '#E7E5E4', sidebarActive: '#44403C' }, dark: { primary: '#D6D3D1', accent: '#F5F5F4', accentHover: '#E7E5E4', background: '#0A090A', surface: '#121112', border: '#282627', sidebar: '#0B0B0C', sidebarBorder: '#282627', sidebarActive: '#D6D3D1' } },
];

export const SIDEBAR_PRESETS: readonly SidebarPreset[] = [
	{ id: 'arma', name: 'ARMA default', light: { sidebar: '#FBFBFB', sidebarBorder: '#E4E4E7', sidebarActive: '#000000' }, dark: { sidebar: '#18181B', sidebarBorder: '#3F3F46', sidebarActive: '#FAFAFA' } },
	{ id: 'paper', name: 'Paper', light: { sidebar: '#FFFFFF', sidebarBorder: '#E4E4E7', sidebarActive: '#0F172A' }, dark: { sidebar: '#09090B', sidebarBorder: '#27272A', sidebarActive: '#FAFAFA' } },
	{ id: 'mist', name: 'Mist', light: { sidebar: '#F1F5F9', sidebarBorder: '#E2E8F0', sidebarActive: '#1E293B' }, dark: { sidebar: '#0F172A', sidebarBorder: '#1E293B', sidebarActive: '#E2E8F0' } },
	{ id: 'ocean', name: 'Ocean tint', light: { sidebar: '#EFF6FF', sidebarBorder: '#DBEAFE', sidebarActive: '#1D4ED8' }, dark: { sidebar: '#0B1220', sidebarBorder: '#1E3A8A', sidebarActive: '#93C5FD' } },
	{ id: 'forest', name: 'Forest tint', light: { sidebar: '#ECFDF5', sidebarBorder: '#D1FAE5', sidebarActive: '#047857' }, dark: { sidebar: '#06140F', sidebarBorder: '#064E3B', sidebarActive: '#6EE7B7' } },
	{ id: 'midnight', name: 'Midnight', light: { sidebar: '#0F172A', sidebarBorder: '#1E293B', sidebarActive: '#F8FAFC' }, dark: { sidebar: '#020617', sidebarBorder: '#1E293B', sidebarActive: '#E2E8F0' } },
	{ id: 'navy', name: 'Navy', light: { sidebar: '#0B2545', sidebarBorder: '#1B3A63', sidebarActive: '#FFFFFF' }, dark: { sidebar: '#06142A', sidebarBorder: '#12284A', sidebarActive: '#DBEAFE' } },
	{ id: 'plum', name: 'Plum', light: { sidebar: '#2E1065', sidebarBorder: '#4C1D95', sidebarActive: '#F5F3FF' }, dark: { sidebar: '#1A0B3B', sidebarBorder: '#3B1D72', sidebarActive: '#DDD6FE' } },
];

const sameColor = (a: string | undefined, b: string | undefined): boolean =>
	String(a ?? '').trim().toUpperCase() === String(b ?? '').trim().toUpperCase();

function matchPreset<K extends string, P extends { light: Record<K, string>; dark: Record<K, string> }>(
	presets: readonly P[],
	keys: readonly K[],
	colors: { light: Partial<Record<K, string>>; dark: Partial<Record<K, string>> },
): P | null {
	return presets.find(preset =>
		(['light', 'dark'] as const).every(theme => keys.every(key => sameColor(colors[theme][key], preset[theme][key]))),
	) ?? null;
}

/** Sidebar values are left out so a sidebar preset picked afterwards keeps the theme preset selected. */
export function matchColorPreset(colors: EditableBranding['colors']): ColorPreset | null {
	return matchPreset(COLOR_PRESETS, [...THEME_COLOR_KEYS, ...SURFACE_COLOR_KEYS], colors);
}

export function matchSidebarPreset(colors: EditableBranding['colors']): SidebarPreset | null {
	return matchPreset(SIDEBAR_PRESETS, SIDEBAR_COLOR_KEYS, colors);
}

// ── Editor document ⇄ API document ─────────────────────────────────────────

const text = (value: unknown): string => (typeof value === 'string' ? value : '');

/** API branding (any subset) → full editable form, with defaults where a value is missing. */
export function toEditableBranding(config?: BrandingConfig | null): EditableBranding {
	const c = config ?? { schemaVersion: 1 };
	const d = DEFAULT_BRANDING;
	const colors = (theme: BrandingTheme): Required<ThemeColors> => {
		const source = c.colors?.[theme] ?? {};
		const fallback = d.colors[theme];
		return {
			primary: text(source.primary) || fallback.primary,
			accent: text(source.accent) || fallback.accent,
			accentHover: text(source.accentHover) || fallback.accentHover,
			background: text(source.background) || fallback.background,
			surface: text(source.surface) || fallback.surface,
			border: text(source.border) || fallback.border,
			sidebar: text(source.sidebar) || fallback.sidebar,
			sidebarBorder: text(source.sidebarBorder) || fallback.sidebarBorder,
			sidebarActive: text(source.sidebarActive) || fallback.sidebarActive,
		};
	};
	return {
		schemaVersion: 1,
		identity: {
			appName: text(c.identity?.appName) || d.identity.appName,
			appTitle: text(c.identity?.appTitle),
			brandName: text(c.identity?.brandName) || d.identity.brandName,
			assistantName: text(c.identity?.assistantName),
		},
		logo: {
			wordmark: { light: text(c.logo?.wordmark?.light), dark: text(c.logo?.wordmark?.dark) },
			mark: { light: text(c.logo?.mark?.light), dark: text(c.logo?.mark?.dark) },
			favicon: text(c.logo?.favicon),
		},
		colors: { light: colors('light'), dark: colors('dark') },
		login: { backgroundImage: text(c.login?.backgroundImage) },
		footer: { text: text(c.footer?.text) },
		email: { senderName: text(c.email?.senderName), logoUrl: text(c.email?.logoUrl) },
	};
}

/**
 * Form → API document. Drops blank values (the API rejects empty strings) and empty groups,
 * trims text, and upper-cases hex colors.
 */
export function compactBranding(form: EditableBranding): BrandingConfig {
	const trimObject = <T extends Record<string, string>>(source: T, hex = false): Partial<T> | undefined => {
		const out: Partial<T> = {};
		for (const [key, value] of Object.entries(source) as [keyof T, string][]) {
			const trimmed = String(value ?? '').trim();
			if (trimmed) out[key] = (hex ? trimmed.toUpperCase() : trimmed) as T[keyof T];
		}
		return Object.keys(out).length ? out : undefined;
	};
	const doc: BrandingConfig = { schemaVersion: 1 };
	const identity = trimObject(form.identity);
	if (identity) doc.identity = identity;
	const wordmark = trimObject(form.logo.wordmark);
	const mark = trimObject(form.logo.mark);
	const favicon = form.logo.favicon.trim();
	if (wordmark || mark || favicon) {
		doc.logo = { ...(wordmark ? { wordmark } : {}), ...(mark ? { mark } : {}), ...(favicon ? { favicon } : {}) };
	}
	const light = withoutDefaultSurfaces('light', trimObject(form.colors.light, true));
	const dark = withoutDefaultSurfaces('dark', trimObject(form.colors.dark, true));
	if (light || dark) doc.colors = { ...(light ? { light } : {}), ...(dark ? { dark } : {}) };
	const login = trimObject(form.login);
	if (login) doc.login = login;
	const footer = trimObject(form.footer);
	if (footer) doc.footer = footer;
	const email = trimObject(form.email);
	if (email) doc.email = email;
	return doc;
}

/** Same document without page surface colors, for APIs that do not accept them yet. */
export function withoutSurfaceColors(config: BrandingConfig): BrandingConfig {
	if (!config.colors) return config;
	const strip = (colors?: ThemeColors): ThemeColors | undefined => {
		if (!colors) return colors;
		const rest = { ...colors };
		for (const key of SURFACE_COLOR_KEYS) delete rest[key];
		return Object.keys(rest).length ? rest : undefined;
	};
	const light = strip(config.colors.light);
	const dark = strip(config.colors.dark);
	const rest: BrandingConfig = { ...config };
	delete rest.colors;
	return light || dark ? { ...rest, colors: { ...(light ? { light } : {}), ...(dark ? { dark } : {}) } } : rest;
}

const SURFACE_FIELD = /^(branding\.)?colors\.(light|dark)\.(background|surface|border)$/;

/** True when a save failed only because the API does not know the page surface keys. */
export function rejectsOnlySurfaceColors(errors: ReadonlyArray<{ field?: string; message?: string }> | null | undefined): boolean {
	return !!errors?.length && errors.every(error => SURFACE_FIELD.test(error.field ?? '') && /unknown field/i.test(error.message ?? ''));
}

/** Surface colors still at the defaults are left out, so the document stays valid for APIs without them. */
function withoutDefaultSurfaces(theme: BrandingTheme, colors: Partial<Required<ThemeColors>> | undefined): Partial<Required<ThemeColors>> | undefined {
	if (!colors) return colors;
	const defaults = DEFAULT_BRANDING.colors[theme];
	if (!SURFACE_COLOR_KEYS.every(key => sameColor(colors[key], defaults[key]))) return colors;
	const rest = { ...colors };
	for (const key of SURFACE_COLOR_KEYS) delete rest[key];
	return Object.keys(rest).length ? rest : undefined;
}

// ── Runtime resolution ─────────────────────────────────────────────────────

export interface ResolvedBranding {
	appName: string;
	appTitle: string;
	brandName: string;
	footerText: string;
	favicon: string;
	wordmark: { light: string; dark: string };
	mark: { light: string; dark: string };
}

/** Field-by-field fallback to {@link DEFAULT_BRANDING}. Empty logo slots stay empty (bundled asset). */
export function resolveBranding(config?: BrandingConfig | null): ResolvedBranding {
	const e = toEditableBranding(config);
	return {
		appName: e.identity.appName,
		appTitle: e.identity.appTitle || DEFAULT_BRANDING.identity.appTitle,
		brandName: e.identity.brandName,
		footerText: e.footer.text || DEFAULT_BRANDING.footer.text,
		favicon: e.logo.favicon,
		wordmark: { ...e.logo.wordmark },
		mark: { ...e.logo.mark },
	};
}

// ── Validation (same rules as BrandingValidator) ───────────────────────────

export type BrandingErrors = Record<string, string>;

const HEX = /^#[0-9A-Fa-f]{6}$/;
const IMAGE_EXT = ['.png', '.svg', '.jpg', '.jpeg', '.webp'];
const FAVICON_EXT = [...IMAGE_EXT, '.ico'];
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001F\u007F-\u009F]/;

/** Public API path prefix for tenant branding images served from S3 via the API. */
export const PLATFORM_BRANDING_ASSET_PATH = '/api/v1/public/branding-assets/';

function endsWithImageExt(path: string, favicon: boolean): boolean {
	const lower = path.toLowerCase();
	return (favicon ? FAVICON_EXT : IMAGE_EXT).some(ext => lower.endsWith(ext));
}

export function isPlatformBrandingAssetUrl(value: string): boolean {
	const trimmed = value.trim();
	if (trimmed.startsWith(PLATFORM_BRANDING_ASSET_PATH)) return true;
	try {
		return new URL(trimmed).pathname.includes(PLATFORM_BRANDING_ASSET_PATH);
	} catch {
		return false;
	}
}

/**
 * Resolve a stored logo URL for browser use. Relative platform paths (and older
 * absolute localhost platform URLs) are rewritten to the current API origin.
 */
export function resolveBrandingAssetUrl(value: string | null | undefined, apiBaseUrl: string): string {
	const raw = (value ?? '').trim();
	if (!raw) return '';
	const base = apiBaseUrl.replace(/\/$/, '');
	if (raw.startsWith(PLATFORM_BRANDING_ASSET_PATH)) {
		return `${base}${raw}`;
	}
	try {
		if (raw.startsWith('http://') || raw.startsWith('https://')) {
			const url = new URL(raw);
			const idx = url.pathname.indexOf(PLATFORM_BRANDING_ASSET_PATH);
			if (idx >= 0) {
				return `${base}${url.pathname.slice(idx)}${url.search}`;
			}
		}
	} catch {
		/* keep raw */
	}
	return raw;
}

function isAllowedImageUrl(value: string, favicon: boolean): boolean {
	const lower = value.toLowerCase();
	if (value.length > 2048 || lower.startsWith('data:') || lower.startsWith('javascript:')) {
		return false;
	}
	if (value.startsWith(PLATFORM_BRANDING_ASSET_PATH)) {
		return endsWithImageExt(value, favicon);
	}
	try {
		const url = new URL(value);
		if (!url.hostname) return false;
		const https = url.protocol === 'https:';
		const platformHttp = url.protocol === 'http:' && isPlatformBrandingAssetUrl(value);
		if (!https && !platformHttp) return false;
		return endsWithImageExt(url.pathname, favicon);
	} catch {
		return false;
	}
}

/** Inline errors keyed by dotted path. `forPublish` adds the publish-only rules. */
export function validateBranding(form: EditableBranding, forPublish: boolean): BrandingErrors {
	const doc = compactBranding(form);
	const errors: BrandingErrors = {};
	const textRule = (path: string, value: string | undefined, max: number, required: boolean, controls = false) => {
		if (value === undefined) {
			if (required) errors[path] = 'Required to publish.';
			return;
		}
		if (value.length > max) errors[path] = `Must be 1–${max} characters.`;
		else if (controls && CONTROL_CHARS.test(value)) errors[path] = 'Must not contain control characters.';
	};
	textRule('identity.appName', doc.identity?.appName, 60, forPublish, true);
	textRule('identity.appTitle', doc.identity?.appTitle, 120, false);
	textRule('identity.brandName', doc.identity?.brandName, 40, forPublish);
	textRule('identity.assistantName', doc.identity?.assistantName, 40, false);

	const urlRule = (path: string, value: string | undefined, favicon = false) => {
		if (value !== undefined && !isAllowedImageUrl(value, favicon)) {
			errors[path] = favicon
				? 'Must be an https URL to a .ico, .png, .svg, .jpg, .jpeg, or .webp file.'
				: 'Must be an https URL ending in .png, .svg, .jpg, .jpeg, or .webp.';
		}
	};
	urlRule('logo.wordmark.light', doc.logo?.wordmark?.light);
	urlRule('logo.wordmark.dark', doc.logo?.wordmark?.dark);
	urlRule('logo.mark.light', doc.logo?.mark?.light);
	urlRule('logo.mark.dark', doc.logo?.mark?.dark);
	urlRule('logo.favicon', doc.logo?.favicon, true);
	urlRule('login.backgroundImage', doc.login?.backgroundImage);
	urlRule('email.logoUrl', doc.email?.logoUrl);

	for (const theme of ['light', 'dark'] as const) {
		const colors = doc.colors?.[theme] ?? {};
		for (const key of [...THEME_COLOR_KEYS, ...SURFACE_COLOR_KEYS, ...SIDEBAR_COLOR_KEYS]) {
			const value = colors[key];
			if (value !== undefined && !HEX.test(value)) errors[`colors.${theme}.${key}`] = 'Must be a hex color like #1A73E8.';
		}
		if (forPublish) {
			const groups: ReadonlyArray<[readonly (keyof ThemeColors)[], string]> = [
				[SIDEBAR_COLOR_KEYS, 'sidebar, sidebarBorder, and sidebarActive must all be set together.'],
				[SURFACE_COLOR_KEYS, 'background, surface, and border must all be set together.'],
			];
			for (const [keys, message] of groups) {
				const present = keys.filter(key => colors[key] !== undefined);
				if (!present.length || present.length === keys.length) continue;
				for (const key of keys) {
					if (colors[key] === undefined) errors[`colors.${theme}.${key}`] = message;
				}
			}
		}
	}

	const footer = doc.footer?.text;
	if (footer !== undefined) {
		if (footer.length > 120) errors['footer.text'] = 'Must be 120 characters or fewer.';
		else if (/[<>]/.test(footer)) errors['footer.text'] = 'Must be plain text.';
	}
	const sender = doc.email?.senderName;
	if (sender !== undefined && (sender.length > 60 || /[<>"\r\n]/.test(sender))) {
		errors['email.senderName'] = 'Must be 60 characters or fewer and must not contain <, >, ", or line breaks.';
	}
	return errors;
}

// ── Contrast ───────────────────────────────────────────────────────────────

function luminance(color: string): number | null {
	const rgb = parseCssColor(color);
	if (!rgb) return null;
	const channel = (c: number) => {
		const v = c / 255;
		return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
	};
	return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);
}

/** WCAG contrast ratio between two hex / rgb colors (1–21). `null` when either is unreadable. */
export function contrastRatio(foreground: string, background: string): number | null {
	const a = luminance(foreground);
	const b = luminance(background);
	if (a === null || b === null) return null;
	const [hi, lo] = a > b ? [a, b] : [b, a];
	return (hi + 0.05) / (lo + 0.05);
}

export function meetsAA(foreground: string, background: string, minimum = 4.5): boolean {
	const ratio = contrastRatio(foreground, background);
	return ratio !== null && ratio >= minimum;
}

/** Text color the app derives for a surface: `#ffffff` or `#0f172a` (same as `_contrast.scss`). */
export function readableOn(background: string): string {
	return pickContrastingForeground(background);
}

/** Blend `a` over `b` (`weight` of a). Both `#RRGGBB`. */
export function mixColors(a: string, b: string, weight: number): string {
	const ca = parseCssColor(a);
	const cb = parseCssColor(b);
	if (!ca || !cb) return a;
	const mix = (x: number, y: number) => Math.round(x * weight + y * (1 - weight)).toString(16).padStart(2, '0');
	return `#${mix(ca.r, cb.r)}${mix(ca.g, cb.g)}${mix(ca.b, cb.b)}`.toUpperCase();
}

/** Muted nav text: the readable color blended toward the background, never below 4.5:1. */
export function mutedTextOn(background: string): string {
	const foreground = readableOn(background);
	for (let weight = 0.62; weight < 1; weight += 0.02) {
		const candidate = mixColors(foreground, background, weight);
		if (meetsAA(candidate, background)) return candidate;
	}
	return foreground;
}

/** Secondary text that stays at 4.5:1 on every one of `backgrounds` (the first sets the direction). */
export function mutedTextOnAll(backgrounds: readonly string[]): string {
	const base = backgrounds[0];
	const foreground = readableOn(base);
	for (let weight = 0.62; weight < 1; weight += 0.02) {
		const candidate = mixColors(foreground, base, weight);
		if (backgrounds.every(background => meetsAA(candidate, background))) return candidate;
	}
	return foreground;
}

function withAlpha(hex: string, alpha: number): string {
	const c = parseCssColor(hex);
	return c ? `rgba(${c.r}, ${c.g}, ${c.b}, ${alpha})` : hex;
}

// ── CSS tokens ─────────────────────────────────────────────────────────────

/** `--primary` / `--accent-surface*` values that differ from the theme's defaults. */
export function deriveColorTokens(theme: BrandingTheme, colors?: ThemeColors | null): Record<string, string> {
	const out: Record<string, string> = {};
	const defaults = DEFAULT_BRANDING.colors[theme];
	const map: Record<ThemeColorKey, string> = { primary: '--primary', accent: '--accent-surface', accentHover: '--accent-surface-hover' };
	for (const key of THEME_COLOR_KEYS) {
		const value = colors?.[key];
		if (value && HEX.test(value) && !sameColor(value, defaults[key])) out[map[key]] = value.toUpperCase();
	}
	return out;
}

/**
 * Sidebar tokens for one theme, or `null` when the theme's three sidebar values are missing,
 * invalid, or equal to the defaults (so today's exact nav colors stay in place).
 */
export function deriveSidebarTokens(theme: BrandingTheme, colors?: ThemeColors | null): Record<string, string> | null {
	const background = colors?.sidebar;
	const border = colors?.sidebarBorder;
	const active = colors?.sidebarActive;
	if (!background || !border || !active || ![background, border, active].every(value => HEX.test(value))) return null;
	const defaults = DEFAULT_BRANDING.colors[theme];
	if (sameColor(background, defaults.sidebar) && sameColor(border, defaults.sidebarBorder) && sameColor(active, defaults.sidebarActive)) {
		return null;
	}
	const foreground = readableOn(background);
	return {
		'--sidebar': background.toUpperCase(),
		'--sidebar-foot-bg': background.toUpperCase(),
		'--sidebar-border': border.toUpperCase(),
		'--sidebar-edge': border.toUpperCase(),
		'--sidebar-border-soft': border.toUpperCase(),
		'--sidebar-fg': foreground,
		'--sidebar-muted-fg': mutedTextOn(background),
		'--sidebar-hover': mixColors(foreground, background, 0.1),
		'--sidebar-active': active.toUpperCase(),
		'--sidebar-active-fg': readableOn(active),
	};
}

/**
 * Page, container, border and text tokens for one theme, or `null` when the three surface values
 * are missing, invalid, or equal to the defaults (so today's exact surfaces stay in place).
 * Text colors are derived for contrast and never stored. Mirrors `_variables*.scss`: in light mode
 * raised panels match the page, in dark mode they match the card surface.
 */
export function deriveSurfaceTokens(theme: BrandingTheme, colors?: ThemeColors | null): Record<string, string> | null {
	const background = colors?.background?.toUpperCase();
	const surface = colors?.surface?.toUpperCase();
	const border = colors?.border?.toUpperCase();
	if (!background || !surface || !border || ![background, surface, border].every(value => HEX.test(value))) return null;
	const defaults = DEFAULT_BRANDING.colors[theme];
	if (sameColor(background, defaults.background) && sameColor(surface, defaults.surface) && sameColor(border, defaults.border)) {
		return null;
	}
	const elevated = theme === 'light' ? background : surface;
	const foreground = readableOn(background);
	const muted = mixColors(readableOn(surface), surface, 0.05);
	const mutedText = mutedTextOnAll([surface, background, elevated, muted]);
	return {
		'--background': background,
		'--foreground': foreground,
		'--text-strong': foreground,
		'--card': surface,
		'--card-foreground': readableOn(surface),
		'--popover': elevated,
		'--popover-foreground': readableOn(elevated),
		'--surface-elevated': elevated,
		'--surface-subtle': surface,
		'--surface-muted': muted,
		'--surface-hover': mixColors(readableOn(surface), surface, 0.07),
		'--secondary': muted,
		'--secondary-foreground': readableOn(muted),
		'--muted': muted,
		'--muted-foreground': mutedText,
		'--text-muted': mutedText,
		'--text-faint': mixColors(foreground, background, 0.45),
		'--table-row-hover': muted,
		'--topbar-bg': withAlpha(surface, theme === 'light' ? 0.3 : 0.85),
		'--statusbar-bg': withAlpha(surface, theme === 'light' ? 0.3 : 0.85),
		'--border': border,
		'--input': border,
		'--input-border': border,
		'--card-border-color': border,
	};
}

/** All custom properties branding sets for one theme (empty when it matches the defaults). */
export function themeTokens(theme: BrandingTheme, config?: BrandingConfig | null): Record<string, string> {
	const colors = config?.colors?.[theme];
	return {
		...deriveColorTokens(theme, colors),
		...(deriveSurfaceTokens(theme, colors) ?? {}),
		...(deriveSidebarTokens(theme, colors) ?? {}),
	};
}

const declarations = (tokens: Record<string, string>): string =>
	Object.entries(tokens).map(([name, value]) => `${name}: ${value};`).join(' ');

/** CSS string for a cssText-safe `url()` value. */
function cssUrl(value: string): string {
	return `url("${value.replace(/["\\\n\r]/g, encodeURIComponent)}")`;
}

/**
 * Stylesheet for published branding. Empty string when nothing differs from today.
 * Light rules use `:root:not([data-theme='dark'])` so they never leak into dark mode.
 */
export function buildBrandingCss(config?: BrandingConfig | null): string {
	if (!config) return '';
	const blocks: string[] = [];
	const light = themeTokens('light', config);
	const dark = themeTokens('dark', config);
	if (Object.keys(light).length) blocks.push(`:root:not([data-theme='dark']) { ${declarations(light)} }`);
	if (Object.keys(dark).length) blocks.push(`:root[data-theme='dark'] { ${declarations(dark)} }`);
	const background = config.login?.backgroundImage;
	if (background && isAllowedImageUrl(background, false)) {
		blocks.push(`:root { --auth-bg-image: ${cssUrl(background)}; }`);
		blocks.push('.auth-page-bg { background-size: cover; background-position: center; }');
	}
	return blocks.join('\n');
}
